import React, { useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Paper,
  Typography,
  Box
} from '@mui/material';
import CitationPanel from '../citationPanel/CitationPanel';
import { ScenarioReference } from '../../api/scenarios';

interface Reference {
  id: number;
  fileName: string;
  pageNumber: number;
  fileId: string;
  text: string;
}

interface ReferencesTableProps {
  references: Reference[] | ScenarioReference[];
  title?: string;
  showContainer?: boolean;
}

const ReferencesTable: React.FC<ReferencesTableProps> = ({ 
  references, 
  title = "References",
  showContainer = true 
}) => {
  const [isCitationOpen, setIsCitationOpen] = useState(false);
  const [selectedReference, setSelectedReference] = useState<Reference | null>(null);

  // Convert ScenarioReference to Reference if needed
  const formattedReferences: Reference[] = references.map((ref: any) => {
    // If it's already in Reference format
    if ('fileName' in ref) {
      return {
        ...ref,
        id: ref.id || Math.floor(Math.random() * 10000),
        fileId: typeof ref.fileId === 'string' ? ref.fileId : ref.fileId.toString(),
        text: ref.text || ''
      } as Reference;
    }
    
    // If it's in ScenarioReference format
    return {
      id: Math.floor(Math.random() * 10000), // Generate a random ID for the reference
      fileName: `File ${ref.fileId}`,
      pageNumber: ref.pageNumber,
      fileId: ref.fileId.toString(),
      text: ''
    };
  });

  const handleViewReference = (reference: Reference) => {
    setSelectedReference(reference);
    setIsCitationOpen(true);
  };

  const renderTable = () => (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>File Name</TableCell>
          <TableCell>Page Number</TableCell>
          <TableCell>Action</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {formattedReferences.map((reference, index) => (
          <TableRow key={reference.id || index}>
            <TableCell>{reference.fileName}</TableCell>
            <TableCell>{reference.pageNumber}</TableCell>
            <TableCell>
              <Button
                onClick={() => handleViewReference(reference)}
              >
                VIEW REFERENCE
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderCitationPanel = () => {
    if (!selectedReference) return null;
    return (
      <CitationPanel
        open={isCitationOpen}
        onClose={() => setIsCitationOpen(false)}
        selectedReference={selectedReference}
      />
    );
  };

  if (showContainer) {
    return (
      <>
        <Paper sx={{ p: 2, mt: 2 }}>
          <Typography variant="h6" gutterBottom color="primary">
            {title}
          </Typography>
          {formattedReferences.length > 0 ? (
            renderTable()
          ) : (
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color="text.secondary">
                No references available
              </Typography>
            </Box>
          )}
        </Paper>
        {renderCitationPanel()}
      </>
    );
  }

  return (
    <>
      {renderTable()}
      {renderCitationPanel()}
    </>
  );
};

export type { Reference };
export default ReferencesTable;
